import { strEquals } from 'journeys/portal/Admin/AdminScreen.helper';
import { Placeholder } from 'journeys/portal/Forms/Sections.constants';
import { FieldHasValue, ValidateField, ValidateNumberField } from 'helpers/Validation';

const PASS = 'Pass';
const FAIL = 'Fail';
const UNKNOWN = 'Unknown';
const PASS_WITH_CORRECTIVE_NOTIFICATION = 'Pass With Corrective Notification';
const FAIL_WITH_CORRECTIVE_NOTIFICATION = 'Fail With Corrective Notification';
const GREEN_ZONE = 'Green Zone';
const YELLOW_ZONE = 'Yellow Zone';
const RED_ZONE = 'Red Zone';
const ACCEPTABLE = 'Acceptable';
const UNACCEPTABLE = 'Unacceptable';
const TEST_MEDIA_CANNOT_BE_LIQUID = 'Test Media Cannot Be Liquid';
const TEST_MEDIA_GAS = 'gas';
const TEST_MEDIA_LIQUID = 'liquid';
const checkAsFoundResultGreenZone = (formData) => strEquals(formData.F102AftResult, GREEN_ZONE);
const checkAsFoundResultYellowZone = (formData) => strEquals(formData.F102AftResult, YELLOW_ZONE);
const checkAsFoundResultRedZone = (formData) => strEquals(formData.F102AftResult, RED_ZONE);

const isReverseFlow = (formData) => {
    const UpstreamPressure = formData?.F1087UpstreamPressure;
    const DownstreamPressure = formData?.F1088DownstreamPressure;

    if (FieldHasValue(UpstreamPressure) && FieldHasValue(DownstreamPressure)) {
        const upstreamPress = ValidateNumberField(UpstreamPressure);
        const downstreamPress = ValidateNumberField(DownstreamPressure);
        return upstreamPress < downstreamPress;
    }
    return false;
};

const allOptions = [
    { name: Placeholder.SELECT_TEST_RESULT, value: '' },
    { name: PASS, value: PASS },
    { name: PASS_WITH_CORRECTIVE_NOTIFICATION, value: PASS_WITH_CORRECTIVE_NOTIFICATION },
    { name: FAIL, value: FAIL },
    { name: FAIL_WITH_CORRECTIVE_NOTIFICATION, value: FAIL_WITH_CORRECTIVE_NOTIFICATION },
    { name: UNKNOWN, value: UNKNOWN }
];

const greenZoneOptions = [
    { name: Placeholder.SELECT_TEST_RESULT, value: '' },
    { name: PASS, value: PASS },
    { name: PASS_WITH_CORRECTIVE_NOTIFICATION, value: PASS_WITH_CORRECTIVE_NOTIFICATION },
];

const yellowZoneOptions = [
    { name: Placeholder.SELECT_TEST_RESULT, value: '' },
    { name: PASS_WITH_CORRECTIVE_NOTIFICATION, value: PASS_WITH_CORRECTIVE_NOTIFICATION },
    { name: FAIL, value: FAIL },
    { name: FAIL_WITH_CORRECTIVE_NOTIFICATION, value: FAIL_WITH_CORRECTIVE_NOTIFICATION },
    { name: UNKNOWN, value: UNKNOWN }
];

const RedZoneOptions = [
    { name: Placeholder.SELECT_TEST_RESULT, value: '' },
    { name: FAIL, value: FAIL },
    { name: FAIL_WITH_CORRECTIVE_NOTIFICATION, value: FAIL_WITH_CORRECTIVE_NOTIFICATION },
    { name: UNKNOWN, value: UNKNOWN }
];

const emptyOptions = [{ name: Placeholder.SELECT_TEST_RESULT, value: '' }];

export const TestResultDropdownOptions = (formData) => {
    if (!formData.F102AftResult) {
        return emptyOptions;
    }
    if (checkAsFoundResultGreenZone(formData)) {
        return greenZoneOptions;
    }
    if (checkAsFoundResultYellowZone(formData)) {
        return yellowZoneOptions;
    }
    if (checkAsFoundResultRedZone(formData)) {
        return RedZoneOptions;
    }
    return allOptions;
};

export const TestAsFoundResultValue = (formData) => {
    if (!formData.F102AftResult) {
        return true;
    }
    if (checkAsFoundResultYellowZone(formData)) {
        const valueArr = yellowZoneOptions.map(opt => opt.value);
        return !valueArr.includes(formData.F85TestResult);
    }
    if (checkAsFoundResultRedZone(formData)) {
        const valueArr = RedZoneOptions.map(opt => opt.value);
        return !valueArr.includes(formData.F85TestResult);
    }
    return false;
};

export const healthBarrierReportFormula = (formData) => {
    const TestMedia = formData?.F1093TestMedia;
    const NitrogenPsvReliefCapacity = formData?.F1096NitrogenPsvReliefCapacity;
    const WaterPsvReliefCapacity = formData?.F1097WaterPsvReliefCapacity;
    const FieldReading = formData?.F1091FieldReading;
    const WaterEquivalentTestFlow = formData?.F1098WaterEquivalentTestFlow;

    if (!FieldHasValue(TestMedia) || !FieldHasValue(FieldReading)) {
        return '';
    }

    const testMedia = String(ValidateField(TestMedia)).toLowerCase();
    if (testMedia === TEST_MEDIA_LIQUID && !FieldHasValue(WaterPsvReliefCapacity)) {
        return TEST_MEDIA_CANNOT_BE_LIQUID;
    }

    if (!isReverseFlow(formData)) {
        return 'Ensure a reverse flow is created (upstream pressure < downstream pressure)';
    }

    if (testMedia === TEST_MEDIA_GAS && FieldHasValue(NitrogenPsvReliefCapacity)) {
        const fieldReading = ValidateNumberField(FieldReading);
        const nitrogenCapacity = ValidateNumberField(NitrogenPsvReliefCapacity);
        return fieldReading <= nitrogenCapacity ? ACCEPTABLE : UNACCEPTABLE;
    }

    if (testMedia === TEST_MEDIA_GAS && !FieldHasValue(NitrogenPsvReliefCapacity)) {
        if (!FieldHasValue(WaterEquivalentTestFlow) || !FieldHasValue(WaterPsvReliefCapacity)) {
            return '';
        }
        const waterEquivFlow = ValidateNumberField(WaterEquivalentTestFlow);
        const waterCapacity = ValidateNumberField(WaterPsvReliefCapacity);
        return waterEquivFlow <= waterCapacity ? ACCEPTABLE : UNACCEPTABLE;
    }

    if (testMedia === TEST_MEDIA_LIQUID && FieldHasValue(WaterPsvReliefCapacity)) {
        const fieldReading = ValidateNumberField(FieldReading);
        const waterCapacity = ValidateNumberField(WaterPsvReliefCapacity);
        return fieldReading <= waterCapacity ? ACCEPTABLE : UNACCEPTABLE;
    }

    return '';
};

export const AsFoundResultP51 = (formData) => {
    const GreenZoneUpperLimit = formData?.F1089GreenZoneUpperLimit;
    const YellowZoneUpperLimit = formData?.F1090YellowZoneUpperLimit;
    const FieldReading = formData?.F1091FieldReading;
    const TestMedia = formData?.F1093TestMedia;
    const WaterPsvReliefCapacity = formData?.F1097WaterPsvReliefCapacity;

    if (!FieldHasValue(FieldReading)) return '';

    if (FieldHasValue(TestMedia)) {
        const testMedia = String(ValidateField(TestMedia)).toLowerCase();
        if (testMedia === TEST_MEDIA_LIQUID && !FieldHasValue(WaterPsvReliefCapacity)) {
            return TEST_MEDIA_CANNOT_BE_LIQUID;
        }
    }

    if (!isReverseFlow(formData)) {
        return 'Ensure a reverse flow is created (upstream pressure < downstream pressure)';
    }

    const fieldReadingValue = ValidateNumberField(FieldReading);
    const greenZoneValue = ValidateNumberField(GreenZoneUpperLimit);
    const yellowZoneValue = ValidateNumberField(YellowZoneUpperLimit);

    if (fieldReadingValue < greenZoneValue) {
        return GREEN_ZONE;
    }
    if (fieldReadingValue >= greenZoneValue && fieldReadingValue < yellowZoneValue) {
        return YELLOW_ZONE;
    }
    if (fieldReadingValue >= yellowZoneValue) {
        return RED_ZONE;
    }
    return '';
};
